import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faEllipsisV,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import ReactModal from "react-modal";
import { useAppData } from "../../context/AppDataContext";
import { useTimeTracking } from "../../context/TimeTrackingContext";
import { useAuthInfo } from "../../context/AuthContext";
import Avatar from "../core/Avatar";
import NovelEditor, { defaultExtensions } from "../core/NovelEditor";
import ActivityLog from "./ActivityLog";
import { errorToast, successfulToast } from "../../util/toastNotifications";
import useClickOutside from "../../hooks/useClickOutside";
import useLinkHoverPopup from "../../hooks/useLinkHoverPopup";
import { canManageTaskComment } from "../../helpers/checkAccess";
import { generateHTML } from "@tiptap/core";
import DOMPurify from "dompurify";
import FilePreview from "../files/FilePreview";
import TaskDependencies from "./TaskDependencies";
import TaskAssignments from "./TaskAssignments";

const CommentActions = ({
  comment,
  openDropdownId,
  onToggleDropdown,
  onEdit,
  onDelete,
  setOpenDropdownId,
}) => {
  const dropdownRef = useClickOutside(() => {
    if (openDropdownId === comment.comment_id) {
      setOpenDropdownId(null);
    }
  });

  return (
    <div className="comment-actions" ref={dropdownRef}>
      <FontAwesomeIcon
        icon={faEllipsisV}
        className="comment-ellipsis"
        onClick={() => onToggleDropdown(comment.comment_id)}
      />
      {openDropdownId === comment.comment_id && (
        <div className="dropdown-menu show">
          <button className="dropdown-item" onClick={onEdit}>
            Edit
          </button>
          <button className="dropdown-item text-danger" onClick={onDelete}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const CommentsActivityLogs = ({ taskId, orgId, task }) => {
  const { api } = useAppData();
  const { activityData } = useTimeTracking();
  const { userInfo } = useAuthInfo();
  const [comments, setComments] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewingLogs, setIsViewingLogs] = useState(false);
  const [newComment, setNewComment] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [commentEditorKey, setCommentEditorKey] = useState(0);
  const [editedCommentIds, setEditedCommentIds] = useState(() => new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [taskCreator, setTaskCreator] = useState();
  const isSubmittingCommentRef = useRef(false);
  const bottomScrollRef = useRef(null);
  const linkPopup = useLinkHoverPopup(bottomScrollRef);
  const draftLoadedRef = useRef(false);
  const draftStorageKey = useMemo(
    () => (taskId ? `task-comment-draft:${taskId}` : null),
    [taskId]
  );

  const createdById = task.created_by_id;
  useEffect(() => {
    if (!createdById) return;

    const userCreatedTask = async () => {
      try {
        const user = await api.user.getUserById(createdById);
        setTaskCreator(user);
      } catch (error) {
        console.error("Failed to fetch task creator", error);
      }
    };
    userCreatedTask();
  }, [createdById]);

  const fullName = `${taskCreator?.first_name} ${taskCreator?.last_name}`;
  const dateTaskCreated = task.date_created;
  const taskCreatedInfo = taskCreator
    ? {
        id: "task-created",
        type: "task-created",
        details: `${fullName} created the task`,
        created_at: dateTaskCreated,
      }
    : null;

  useEffect(() => {
    if (!draftStorageKey || typeof window === "undefined") {
      return;
    }

    const saved = window.sessionStorage.getItem(draftStorageKey);
    draftLoadedRef.current = true;
    if (!saved) {
      setNewComment(null);
      setCommentEditorKey((prev) => prev + 1);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      setNewComment(parsed);
    } catch {
      setNewComment(saved);
    }
    setCommentEditorKey((prev) => prev + 1);
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey || typeof window === "undefined") {
      return;
    }

    if (!draftLoadedRef.current) {
      return;
    }

    if (newComment == null) return;

    try {
      window.sessionStorage.setItem(
        draftStorageKey,
        JSON.stringify(newComment)
      );
    } catch {}
  }, [draftStorageKey, newComment]);

  const fetchComments = async () => {
    const commentsData = await api.taskComment.getCommentsByTask(taskId);
    setComments(commentsData || []);
  };

  const fetchActivityLogs = async () => {
    try {
      const logs = await api.activityLog.getByTask(taskId);
      setActivityLogs(logs || []);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      setActivityLogs([]);
    }
  };
  const fetchAll = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchComments(), fetchActivityLogs()]);
    } catch (error) {
      console.error("Error fetching comments/activity logs:", error);
      errorToast("Failed to load comments or activity logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchAll();
    }
  }, [taskId]);

  useEffect(() => {
    if (!activityData || activityData.task_id !== taskId) {
      return;
    }
    setActivityLogs((prev) => {
      const hasId = activityData.activity_log_id
        ? prev.some(
            (log) => log.activity_log_id === activityData.activity_log_id
          )
        : prev.some(
            (log) =>
              log.details === activityData.details &&
              getRecordDate(log) === getRecordDate(activityData)
          );

      if (hasId) {
        return prev;
      }

      return [...prev, activityData];
    });
  }, [activityData, taskId]);

  const activityRefreshKey = useMemo(() => {
    const assignmentIds = (task?.assignments || [])
      .map((assignment) => assignment.user_id)
      .sort()
      .join(",");
    const tagIds = (task?.tags || [])
      .map((tag) => tag.tag_id)
      .sort()
      .join(",");
    return `${task?.date_updated || ""}|${assignmentIds}|${tagIds}`;
  }, [task?.date_updated, task?.assignments, task?.tags]);

  useEffect(() => {
    if (taskId) {
      fetchActivityLogs();
    }
  }, [taskId, activityRefreshKey]);

  const handleAddComment = async () => {
    if (!newComment) {
      return;
    }

    const commentText = JSON.stringify(newComment);
    if (!commentText || commentText === '""' || commentText === "null") {
      return;
    }

    if (isSubmittingCommentRef.current) return;
    isSubmittingCommentRef.current = true;
    setIsSubmittingComment(true);

    try {
      await api.taskComment.createComment({
        task_id: taskId,
        org_id: orgId,
        user_id: userInfo.user_id,
        comment_text: commentText,
      });
      setNewComment(null);
      setCommentEditorKey((prev) => prev + 1);
      if (draftStorageKey && typeof window !== "undefined") {
        window.sessionStorage.removeItem(draftStorageKey);
      }
      successfulToast("Comment added");
      fetchComments();
    } catch (error) {
      console.error("Error adding comment:", error);
      errorToast("Failed to add comment");
    } finally {
      isSubmittingCommentRef.current = false;
      setIsSubmittingComment(false);
    }
  };

  const handleUpdateComment = async (commentId) => {
    if (!editText) {
      setEditingCommentId(null);
      return;
    }

    const commentText = JSON.stringify(editText);
    if (!commentText || commentText === '""' || commentText === "null") {
      setEditingCommentId(null);
      return;
    }

    try {
      const response = await api.taskComment.updateComment(commentId, {
        comment_text: commentText,
      });
      setEditingCommentId(null);
      setEditText(null);
      setEditedCommentIds((prev) => new Set(prev).add(commentId));
      successfulToast("Comment updated");
      if (response?.activity_log) {
        setActivityLogs((prev) => [...prev, response.activity_log]);
      } else {
        fetchActivityLogs();
      }
      fetchComments();
    } catch (error) {
      console.error("Error updating comment:", error);
      errorToast("Failed to update comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await api.taskComment.deleteComment(commentId);
      successfulToast("Comment deleted");
      if (response?.activity_log) {
        setActivityLogs((prev) => [...prev, response.activity_log]);
      } else {
        fetchActivityLogs();
      }
      fetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      errorToast("Failed to delete comment");
    }
  };

  const startEditing = (comment) => {
    setEditingCommentId(comment.comment_id);
    setOpenDropdownId(null);
    try {
      const parsed = JSON.parse(comment.comment_text);
      setEditText(parsed);
    } catch {
      setEditText(comment.comment_text);
    }
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditText(null);
  };

  const handleToggleDropdown = (commentId) => {
    setOpenDropdownId(openDropdownId === commentId ? null : commentId);
  };

  const handleDeleteClick = (commentId) => {
    setOpenDropdownId(null);
    setCommentToDelete(commentId);
    setShowDeleteModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    const datePart = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart} | ${timePart}`;
  };

  const commentHtmlCacheRef = useRef(new Map());

  const renderCommentAsHTML = (commentText) => {
    const cache = commentHtmlCacheRef.current;
    if (cache.has(commentText)) return cache.get(commentText);
    let html;
    try {
      const jsonContent = JSON.parse(commentText);
      html = DOMPurify.sanitize(generateHTML(jsonContent, defaultExtensions));
    } catch {
      html = DOMPurify.sanitize(commentText);
    }
    cache.set(commentText, html);
    return html;
  };

  const handleCommentClick = (event) => {
    const target = event.target;
    if (target && target.tagName === "IMG") {
      event.preventDefault();
      event.stopPropagation();
      setPreviewFile({
        file_type: "image/*",
        file_url: target.getAttribute("src"),
        file_name: target.getAttribute("alt") || "image",
      });
    }
  };

  const getRecordDate = (record) =>
    record?.created_at || record?.date_created || record?.date_updated;

  const combinedItems = useMemo(() => {
    const activityItems = isViewingLogs
      ? [...activityLogs].sort(
          (a, b) => new Date(getRecordDate(a)) - new Date(getRecordDate(b))
        )
      : [];
    const orderedComments = [...comments].sort(
      (a, b) => new Date(getRecordDate(a)) - new Date(getRecordDate(b))
    );
    const items = [
      ...(taskCreatedInfo ? [taskCreatedInfo] : []),
      ...activityItems,
      ...orderedComments,
    ];
    return items.sort(
      (a, b) => new Date(getRecordDate(a)) - new Date(getRecordDate(b))
    );
  }, [comments, activityLogs, isViewingLogs, taskCreatedInfo]);

  useEffect(() => {
    if (bottomScrollRef.current) {
      bottomScrollRef.current.scrollTop = bottomScrollRef.current.scrollHeight;
    }
  }, [combinedItems]);

  return (
    <div className="comments-activity-logs-container">
      {linkPopup}
      <div className="buttons-wrapper">
        <button
          className="activity-button"
          onClick={() => setIsViewingLogs((prev) => !prev)}
        >
          {isViewingLogs ? "Hide" : "Show"} Activity
        </button>
      </div>

      <div className="comments-activity-content">
        <div className="content-wrapper" ref={bottomScrollRef}>
          {loading ? (
            <div className="loading">Loading comments...</div>
          ) : combinedItems.length === 0 ? (
            <div className="no-comments">
              No comments yet. Be the first to comment!
            </div>
          ) : (
            combinedItems.map((record) => {
              if (record.activity_log_id || record.created_at) {
                return (
                  <ActivityLog
                    key={record.activity_log_id || record.created_at}
                    activityData={record}
                  />
                );
              }

              return (
                <div key={record.comment_id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-author">
                      <Avatar userData={record.user} />

                      <div className="comment-author-info">
                        <div className="comment-author-name">
                          {record.user?.first_name} {record.user?.last_name}
                        </div>
                      </div>
                    </div>
                    <div className="comment-meta">
                      <div className="comment-date">
                        {formatDate(record.date_created)}
                        {editedCommentIds.has(record.comment_id) && (
                          <span className="comment-edited"> (edited)</span>
                        )}
                      </div>
                      {canManageTaskComment(userInfo, record) &&
                        editingCommentId !== record.comment_id && (
                          <CommentActions
                            comment={record}
                            openDropdownId={openDropdownId}
                            onToggleDropdown={handleToggleDropdown}
                            onEdit={() => startEditing(record)}
                            onDelete={() =>
                              handleDeleteClick(record.comment_id)
                            }
                            setOpenDropdownId={setOpenDropdownId}
                          />
                        )}
                    </div>
                  </div>
                  <div className="comment-content">
                    {editingCommentId === record.comment_id ? (
                      <div className="comment-edit-wrapper">
                        <NovelEditor
                          initialValue={editText}
                          onChange={setEditText}
                          onImageClick={({ src, alt }) =>
                            setPreviewFile({
                              file_type: "image/*",
                              file_url: src,
                              file_name: alt || "image",
                            })
                          }
                        />
                        <div className="comment-edit-actions">
                          <button
                            className="btn btn-secondary"
                            onClick={cancelEditing}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={() =>
                              handleUpdateComment(record.comment_id)
                            }
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="comment-text"
                        onClick={handleCommentClick}
                        dangerouslySetInnerHTML={{
                          __html: renderCommentAsHTML(record.comment_text),
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="comment-form">
          <div className="info-label">
            <FontAwesomeIcon icon={faComment} className="mr-2" />
            Comment
          </div>
          <NovelEditor
            key={commentEditorKey}
            initialValue={newComment}
            onChange={setNewComment}
            onImageClick={({ src, alt }) =>
              setPreviewFile({
                file_type: "image/*",
                file_url: src,
                file_name: alt || "image",
              })
            }
          />
          <div className="draft-status-sumbmit-button-wrapper">
            {newComment && (
              <div
                className="comment-draft-status"
                style={{ fontSize: "0.85rem", color: "#666" }}
              >
                Draft saved
              </div>
            )}
            <button
              className="btn btn-primary mt-2 comment-submit-button"
              onClick={handleAddComment}
              disabled={
                isSubmittingComment ||
                !newComment ||
                JSON.stringify(newComment) ===
                  '{"type":"doc","content":[{"type":"paragraph"}]}'
              }
            >
              {isSubmittingComment ? "Adding..." : "Add Comment"}
            </button>
          </div>
        </div>
      </div>

      <ReactModal
        isOpen={showDeleteModal}
        onRequestClose={() => setShowDeleteModal(false)}
        className="delete-modal"
        overlayClassName="delete-modal-overlay"
      >
        <div className="icon">
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <div className="are-you-sure">
          Are you sure you want to delete this comment?
        </div>
        <button
          className="cancel-button"
          onClick={() => {
            setShowDeleteModal(false);
            setCommentToDelete(null);
          }}
        >
          Cancel
        </button>
        <button
          className="delete-button"
          onClick={async () => {
            if (commentToDelete) {
              await handleDeleteComment(commentToDelete);
            }
            setShowDeleteModal(false);
            setCommentToDelete(null);
          }}
        >
          Yes
        </button>
      </ReactModal>

      <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} />
    </div>
  );
};

export default CommentsActivityLogs;
